import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ChangeEvent } from 'react';
import { Link } from 'react-router-dom';
import type { Role, SetRoleInput, User } from '@clan-heart/shared';
import { canAssignRole } from '@clan-heart/shared';
import { getMembers, setMemberRole } from '../api.js';
import { RoleBadge } from '../components/RoleBadge.js';
import styles from './LeaderboardPage.module.css';

/** `owner` is never an assignable target — see `canAssignRole`. */
const ASSIGNABLE_ROLES: Role[] = ['first_officer', 'officer', 'member'];
const ROLE_LABELS: Record<Role, string> = {
  owner: 'owner',
  first_officer: 'First officer',
  officer: 'officer',
  member: 'member',
};

/** Which roles `viewerRole` may set `targetRole` to — always includes
 * `targetRole` itself when the viewer may touch this member at all, so the
 * select's current value is always one of its own options. */
function assignableRolesFor(viewerRole: Role, targetRole: Role): Role[] {
  return ASSIGNABLE_ROLES.filter((next) => canAssignRole(viewerRole, targetRole, next));
}

export function LeaderboardPage({ me }: { me: User }) {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['members'], queryFn: getMembers });

  const roleMutation = useMutation({
    mutationFn: ({ id, role }: { id: string; role: SetRoleInput['role'] }) => setMemberRole(id, role),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['members'] }),
  });

  if (query.isLoading) return <p>Loading…</p>;
  if (query.isError) return <p>{(query.error as Error).message}</p>;

  const members = query.data?.members ?? [];

  function handleRoleChange(id: string, event: ChangeEvent<HTMLSelectElement>) {
    const role = event.target.value as SetRoleInput['role'];
    roleMutation.mutate({ id, role });
  }

  // whose cannons are fired the most: marked in the Broadsides column
  const mostBroadsides = Math.max(0, ...members.map((m) => m.broadsides));

  return (
    <section>
      <h2 className={styles.heading}>Leaderboard</h2>
      <table className={styles.table}>
        <thead>
          <tr>
            <th scope="col">Rank</th>
            <th scope="col">Member</th>
            <th scope="col">Role</th>
            <th scope="col">Approved</th>
            <th scope="col">Points</th>
            <th scope="col" title="Broadsides fired from their ship on their profile">
              Broadsides
            </th>
          </tr>
        </thead>
        <tbody>
          {members.map((member, index) => {
            const assignable = assignableRolesFor(me.role, member.role);
            return (
              <tr key={member.id}>
                <td className={styles.rank}>{index + 1}</td>
                <td>
                  <div className={styles.member}>
                    {member.avatarUrl ? (
                      <img className={styles.avatar} src={member.avatarUrl} alt="" />
                    ) : null}
                    {member.profileSlug ? (
                      <Link className={styles.memberLink} to={`/crew/${member.profileSlug}`}>
                        {member.displayName}
                      </Link>
                    ) : (
                      <span>{member.displayName}</span>
                    )}
                  </div>
                </td>
                <td>
                  {assignable.length > 0 ? (
                    <div className={styles.roleCell}>
                      <label className={styles.srOnly} htmlFor={`role-${member.id}`}>
                        Role for {member.displayName}
                      </label>
                      <select
                        id={`role-${member.id}`}
                        className={styles.roleSelect}
                        value={member.role}
                        onChange={(event) => handleRoleChange(member.id, event)}
                      >
                        {assignable.map((role) => (
                          <option key={role} value={role}>
                            {ROLE_LABELS[role]}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <RoleBadge role={member.role} />
                  )}
                </td>
                <td>{member.approvedCount}</td>
                <td className={styles.points}>{member.points}</td>
                <td className={member.broadsides > 0 && member.broadsides === mostBroadsides ? styles.mostFired : undefined}>
                  {member.broadsides}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}
